import { userSetPushSubscription } from "../thunks/user";

export function hasServiceWorkerSupport(): boolean {
    return typeof window !== 'undefined' && 'serviceWorker' in navigator;
}

export function hasWebPushSupport(): boolean {
    return hasServiceWorkerSupport()
        && 'Notification' in window
        && 'PushManager' in window;
}

let cachedVapidPublicKey: string | null = null;
let vapidKeyPromise: Promise<string | null> | null = null;

export async function getVapidPublicKey(): Promise<string | null> {
    if (cachedVapidPublicKey !== null) {
        return cachedVapidPublicKey;
    }

    if (vapidKeyPromise) {
        return vapidKeyPromise;
    }

    vapidKeyPromise = (async () => {
        try {
            const response = await fetch("/api/getvapidpubkey");

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            cachedVapidPublicKey = (await response.text()).trim();

            return cachedVapidPublicKey;
        } catch (err) {
            console.error("Error fetching VAPID public key:", err);
            return null;
        } finally {
            vapidKeyPromise = null;
        }
    })();

    return vapidKeyPromise;
}

/**
 * Gets the service worker registration
 * @returns The service worker registration or null if not available
 */
export async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (!hasServiceWorkerSupport()) {
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        return registration;
    } catch (err) {
        console.error('Error getting service worker registration:', err);
        return null;
    }
}

/**
 * Returns true if push permission is granted
 * @param promptForPermissionIfNeeded Whether to prompt the user for permission if not already granted.
 */
export async function isPushPermissionGranted(
    promptForPermissionIfNeeded: boolean = false,
): Promise<boolean> {
    if (!hasWebPushSupport()) {
        return false;
    }

    const shouldPrompt = promptForPermissionIfNeeded
        && Notification.permission === 'default';

    const permission = shouldPrompt
        ? await Notification.requestPermission()
        : Notification.permission;

    return permission === 'granted';
}

/**
 * Gets a push subscription if exists. Be sure to check for permission first.
 * @returns A push subscription or null if not available
 */
export async function getSubscriptionIfExists(): Promise<PushSubscription | null> {
    if (!hasWebPushSupport()) {
        return null;
    }

    if (Notification.permission !== 'granted') {
        return null;
    }

    const registration = await getServiceWorkerRegistration();
    if (!registration) {
        return null;
    }

    try {
        const subscription = await registration.pushManager.getSubscription();
        return subscription;
    } catch (err) {
        console.error('Error getting push subscription:', err);
        return null;
    }
}

/**
 * Attempts to create a new push subscription. Be sure to check for permission first.
 * @returns A push subscription or null if not available
 */
export async function createNewSubscription(): Promise<PushSubscription | null> {
    if (!hasWebPushSupport()) {
        return null;
    }

    if (Notification.permission !== 'granted') {
        return null;
    }

    const registration = await getServiceWorkerRegistration();
    if (!registration) {
        return null;
    }

    const vapidPublicKey = await getVapidPublicKey();
    if (!vapidPublicKey) {
        console.warn('VAPID public key not available');
        return null;
    }

    try {
        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidPublicKey,
        });

        return subscription;
    } catch (err) {
        console.error('Error subscribing to push notifications:', err);
        return null;
    }
}

export async function syncSubscriptionToServer(subscription: PushSubscription): Promise<boolean> {
    if (!hasWebPushSupport()) {
        return false;
    }

    try {
        await userSetPushSubscription(
            subscription.endpoint,
            JSON.stringify(subscription.toJSON?.() ?? subscription),
        );

        return true;
    } catch (err) {
        console.error("Error syncing push subscription on server:", err);
        return false;
    }
}

export async function unsubscribe(): Promise<boolean> {
    if (!hasWebPushSupport()) {
        return false;
    }

    const registration = await getServiceWorkerRegistration();
    if (!registration) {
        return false;
    }

    try {
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            return true; // already unsubscribed
        }

        await subscription.unsubscribe();
        return true;
    } catch (err) {
        console.error("Error unsubscribing from push notifications:", err);
        return false;
    }
}
